import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { ESchoolYearStatus } from 'common/enum/misc.enum';
import { computed, ComputedRef, Ref } from 'vue';
import { usePermissions } from './usePermissions';
import { useSchoolYearStatus } from './useSchoolYearStatus';

interface ISchoolYearsComposable {
  allSchoolYearsTenant: ComputedRef<Components.Schemas.SchoolYearDto[]>;
  isSchoolYearsTenantLoading: Ref<boolean>;
  currentSchoolYear: ComputedRef<Components.Schemas.SchoolYearDto | null>;
  currentOrNextSchoolYear: ComputedRef<Components.Schemas.SchoolYearDto | null>;
  getStatus: (schoolYear: Components.Schemas.SchoolYearDto) => ESchoolYearStatus;
  isSchoolYearInFuture: (schoolYear: Components.Schemas.SchoolYearDto) => boolean;
  lastCreatedSchoolYear: ComputedRef<Components.Schemas.SchoolYearDto | undefined>;
  getStatusName: (schoolYear: Components.Schemas.SchoolYearDto) => string;
  currentAndUpcomingSchoolYears: ComputedRef<Components.Schemas.SchoolYearDto[]>;
}

// Tenant-facing school-year reads + derived selectors. Pure status helpers come from useSchoolYearStatus;
// provider list + create/edit/delete live in composables/provider/useProviderSchoolYears.
export function useSchoolYears(): ISchoolYearsComposable {
  const { hasTenantAdminAccess } = usePermissions();
  const { getStatus, getStatusName, isSchoolYearInFuture } = useSchoolYearStatus();

  const getSchoolYearsTenant = async (): Promise<Components.Schemas.SchoolYearDto[]> => {
    const result = await Api.Routes.SchoolYear.retrieveForTenant({
      $filter: {
        filter: [],
        order: [{ field: 'year', dir: 'desc' }],
      },
    });
    return result.rows;
  };

  const schoolYearListTenantQueryKey = computed(() => ['schoolYears', 'list', 'tenant', hasTenantAdminAccess.value]);
  const queryTenant = useQueryWithToast({
    queryKey: schoolYearListTenantQueryKey,
    queryFn: () => getSchoolYearsTenant(),
  });

  const allSchoolYearsTenant = computed<Components.Schemas.SchoolYearDto[]>(() => queryTenant.data.value ?? []);
  const isSchoolYearsTenantLoading = computed<boolean>(
    () => queryTenant.isLoading.value || queryTenant.isFetching.value,
  );

  const currentSchoolYear = computed<Components.Schemas.SchoolYearDto | null>(() => {
    return allSchoolYearsTenant.value.find((schoolYear) => getStatus(schoolYear) === ESchoolYearStatus.ACTIVE) ?? null;
  });

  const currentOrNextSchoolYear = computed<Components.Schemas.SchoolYearDto | null>(() => {
    if (currentSchoolYear.value) {
      return currentSchoolYear.value;
    }

    return (
      allSchoolYearsTenant.value
        .filter((sy) => getStatus(sy) === ESchoolYearStatus.UPCOMING)
        .sort((a, b) => new Date(a.schoolYearStart).getTime() - new Date(b.schoolYearStart).getTime())[0] ?? null
    );
  });

  const lastCreatedSchoolYear = computed(() => {
    return allSchoolYearsTenant.value
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  });

  const currentAndUpcomingSchoolYears = computed<Components.Schemas.SchoolYearDto[]>(() => {
    const statusOrder: Record<ESchoolYearStatus, number> = {
      [ESchoolYearStatus.ACTIVE]: 0,
      [ESchoolYearStatus.UPCOMING]: 1,
      [ESchoolYearStatus.PAST]: 2,
    };

    return allSchoolYearsTenant.value
      .filter((sy) => getStatus(sy) !== ESchoolYearStatus.PAST)
      .slice()
      .sort((a, b) => {
        const sa = statusOrder[getStatus(a)];
        const sb = statusOrder[getStatus(b)];
        if (sa !== sb) return sa - sb;
        return new Date(a.schoolYearStart).getTime() - new Date(b.schoolYearStart).getTime();
      });
  });

  return {
    allSchoolYearsTenant,
    isSchoolYearsTenantLoading,
    currentSchoolYear,
    currentOrNextSchoolYear,
    getStatus,
    isSchoolYearInFuture,
    lastCreatedSchoolYear,
    getStatusName,
    currentAndUpcomingSchoolYears,
  };
}
